import { useState } from 'react';
import { router, usePage } from '@inertiajs/react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Grid, Slide, styled } from '@mui/material';
import { EmailCpf } from '@/packages/auth/components/email-cpf';
import { Password } from '@/packages/auth/components/password';
import logoGalenaEdupass from '@/assets/images/logos/galena.svg';
import loginBg from '@/assets/images/png/login-bg.png';
import { session_path } from '@/utils/routes';

type LoginStep = 'identifier' | 'password';

const StyledImageSection = styled('div')(() => ({
  backgroundImage: `url('${loginBg}')`,
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
}));

const BackButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: theme.spacing(2),
}));

interface PageProps {
  errors?: {
    identifier?: string;
    password?: string;
    base?: string;
  };
  [key: string]: unknown;
}

const IDENTIFIER_STORAGE_KEY = 'login_identifier';

export default function LoginPage() {
  const { props } = usePage<PageProps>();
  const { errors = {} } = props;

  // Restore identifier from sessionStorage if returning with errors
  const savedIdentifier = sessionStorage.getItem(IDENTIFIER_STORAGE_KEY) || '';
  const hasError = Boolean(errors.base || errors.password);

  const [step, setStep] = useState<LoginStep>(
    hasError && savedIdentifier ? 'password' : 'identifier'
  );
  const [identifier, setIdentifier] = useState(hasError ? savedIdentifier : '');
  const [processing, setProcessing] = useState(false);

  const handleIdentifierSubmit = (value: string) => {
    setIdentifier(value);
    sessionStorage.setItem(IDENTIFIER_STORAGE_KEY, value);
    setStep('password');
  };

  const handlePasswordSubmit = (password: string) => {
    setProcessing(true);
    router.post(
      session_path(),
      { identifier, password },
      {
        onFinish: () => setProcessing(false),
        onSuccess: () => sessionStorage.removeItem(IDENTIFIER_STORAGE_KEY),
      }
    );
  };

  const handleBack = () => {
    setStep('identifier');
  };

  return (
    <Grid container minHeight="100vh" bgcolor="white" position="relative">
      {step === 'password' && (
        <BackButton onClick={handleBack}>
          <ArrowBackIcon fontSize="small" />
          voltar
        </BackButton>
      )}

      <Grid
        size={{ xs: 12, md: 6 }}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Box width="100%" maxWidth={360} position="relative">
          <img src={logoGalenaEdupass} alt="Logo da Galena" width={157} />

          <Box minHeight={step === 'identifier' ? 350 : 120} mt={4}>
            <Slide direction="right" in={step === 'identifier'} appear={false}>
              <Box position="absolute" width="100%">
                <EmailCpf nextStep={handleIdentifierSubmit} />
              </Box>
            </Slide>

            <Slide direction="left" in={step === 'password'} appear={false}>
              <Box position="absolute" width="100%">
                <Password
                  identifier={identifier}
                  onSubmit={handlePasswordSubmit}
                  error={errors.password || errors.base}
                  processing={processing}
                />
              </Box>
            </Slide>
          </Box>
        </Box>
      </Grid>

      <Grid size={{ xs: 0, md: 6 }} display={{ xs: 'none', md: 'flex' }}>
        <StyledImageSection />
      </Grid>
    </Grid>
  );
}
